import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Form,
  Switch,
  InputNumber,
  Button,
  message,
  Space,
  Alert,
  Divider,
  Row,
  Col,
  Select
} from 'antd';
import {
  ApiOutlined,
  SaveOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';

const { Option } = Select;

export default function Network() {
  const { domainData, reloadDomain } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (domainData) {
      form.setFieldsValue({
        proxy_timeout: domainData.proxy_timeout || 60,
        max_body_size: domainData.max_body_size || 0,
        http_version: domainData.http_version || '1.1',
        keep_alive: domainData.keep_alive !== undefined ? domainData.keep_alive : true,
        keepalive_timeout: domainData.keepalive_timeout || 60,
        websocket_enabled: !!domainData.websocket_enabled,
        l2_policy: domainData.l2_policy === 'force_l2' ? 'on' : (domainData.l2_policy || 'auto')
      });
    }
  }, [domainData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const response = await cdnService.updateDomain({
        id: domainData.id,
        proxy_timeout: values.proxy_timeout,
        max_body_size: values.max_body_size ?? 0,
        http_version: values.http_version,
        keep_alive: values.keep_alive,
        keepalive_timeout: values.keepalive_timeout,
        websocket_enabled: !!values.websocket_enabled,
        l2_policy: values.l2_policy
      });

      if (response.success) {
        message.success('保存成功');
        reloadDomain();
      } else {
        message.error(response.error || '保存失败');
      }
    } catch (error) {
      message.error('保存失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Card
        title={
          <Space>
            <ApiOutlined />
            网络设置
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSubmit}
            loading={loading}
          >
            保存设置
          </Button>
        }
      >
        <Alert
          message="网络配置"
          description="配置CDN与源站之间的网络连接参数，以及客户端连接设置。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form
          form={form}
          layout="vertical"
        >
          <Divider orientation="left">回源协议</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="回源 HTTP 版本"
                name="http_version"
                tooltip="CDN回源时使用的HTTP协议版本"
              >
                <Select>
                  <Option value="1.1">HTTP/1.1</Option>
                  <Option value="2">HTTP/2（源站不支持时自动回退 1.1）</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="WebSocket" name="websocket_enabled" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">连接设置</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="代理超时 (秒)"
                name="proxy_timeout"
                tooltip="等待源站响应的最长时间（建连固定 5 秒；WebSocket 不受此限）"
                rules={[
                  { required: true, message: '请输入超时时间' },
                  { type: 'number', min: 1, max: 3600, message: '范围: 1-3600秒' }
                ]}
              >
                <InputNumber
                  min={1}
                  max={3600}
                  style={{ width: '100%' }}
                  placeholder="60"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Keep-Alive"
                name="keep_alive"
                valuePropName="checked"
                tooltip="启用HTTP Keep-Alive，复用TCP连接提高性能"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="请求体上限 (MB)"
                name="max_body_size"
                tooltip="超过即返回 413；0 = 使用节点默认上限"
              >
                <InputNumber min={0} max={10240} style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.keep_alive !== cur.keep_alive}>
            {({ getFieldValue }) =>
              getFieldValue('keep_alive') ? (
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label="Keep-Alive 超时 (秒)"
                      name="keepalive_timeout"
                      tooltip="空闲连接保持时间，超过后关闭连接释放资源"
                      rules={[
                        { type: 'number', min: 1, max: 3600, message: '范围: 1-3600秒' }
                      ]}
                    >
                      <InputNumber
                        min={1}
                        max={3600}
                        style={{ width: '100%' }}
                        placeholder="60"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              ) : null
            }
          </Form.Item>

          <Divider orientation="left">回源路由</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="二级缓存"
                name="l2_policy"
                tooltip="开启后边缘未命中先经中间层取；中间层不可用时自动直连。「直连且不缓存」会关闭整域缓存"
              >
                <Select>
                  <Option value="auto">关闭(直连源站)</Option>
                  <Option value="on">开启</Option>
                  <Option value="no_cache_direct">直连且不缓存</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="提示"
            description="网络设置的更改将在几分钟内生效。如果启用WebSocket，建议将代理超时设置为300秒或更长。"
            type="warning"
            showIcon
            style={{ marginTop: 24 }}
          />
        </Form>
      </Card>
    </div>
  );
}
